import axios from "axios"
import { api, API_BASE_URL } from "@/lib/axios"
import type {
  SellingType,
  SellingTypesResponse,
  SellingTypeDetailResponse,
  CreateSellingTypePayload,
  UpdateSellingTypePayload,
  WirePrice,
  WirePricesResponse,
  WirePriceDetailResponse,
  CreateWirePricePayload,
  UpdateWirePricePayload,
  SellingPrice,
  SellingPricesResponse,
  SellingPriceDetailResponse,
  CreateSellingPricePayload,
  UpdateSellingPricePayload,
  ProductQuantity,
  ProductQuantitiesResponse,
  Product,
  ProductsResponse,
  AvailableProduct,
  AvailableProductsResponse,
  ProductRequestPayload,
  ProductRequestResponse,
  Sell,
  SellsResponse,
  SellDetailResponse,
  CreateSellPayload,
  BudgetCalculationPrice,
  BudgetCalculationPricesResponse,
  BudgetCalculationPriceDetailResponse,
  UpdateBudgetCalculationPricePayload,
  MaterialColor,
  MaterialColorsResponse,
  WireType,
  WireTypesResponse,
} from "@/types/products"

// Selling Types
export async function fetchSellingTypes(): Promise<SellingType[]> {
  const { data } = await api.get<SellingTypesResponse>("/products/selling-type/")
  return data.data
}

export async function createSellingType(payload: CreateSellingTypePayload): Promise<SellingType> {
  const { data } = await api.post<SellingTypeDetailResponse>("/products/selling-type/", payload)
  return data.data
}

export async function updateSellingType(id: number, payload: UpdateSellingTypePayload): Promise<SellingType> {
  const { data } = await api.patch<SellingTypeDetailResponse>(`/products/selling-type/${id}/`, payload)
  return data.data
}

export async function deleteSellingType(id: number): Promise<void> {
  await api.delete(`/products/selling-type/${id}/`)
}

// Wire Prices
export async function fetchWirePrices(): Promise<WirePrice[]> {
  const { data } = await api.get<WirePricesResponse>("/products/wire-prices/")
  return data.data
}

export async function createWirePrice(payload: CreateWirePricePayload): Promise<WirePrice> {
  const { data } = await api.post<WirePriceDetailResponse>("/products/wire-prices/", payload)
  return data.data
}

export async function updateWirePrice(id: number, payload: UpdateWirePricePayload): Promise<WirePrice> {
  const { data } = await api.patch<WirePriceDetailResponse>(`/products/wire-prices/${id}/`, payload)
  return data.data
}

export async function deleteWirePrice(id: number): Promise<void> {
  await api.delete(`/products/wire-prices/${id}/`)
}

// Selling Prices
export async function fetchSellingPrices(): Promise<SellingPrice[]> {
  const { data } = await api.get<SellingPricesResponse>("/products/selling-prices/")
  return data.data
}

export async function createSellingPrice(payload: CreateSellingPricePayload): Promise<SellingPrice> {
  const { data } = await api.post<SellingPriceDetailResponse>("/products/selling-prices/", payload)
  return data.data
}

export async function updateSellingPrice(id: number, payload: UpdateSellingPricePayload): Promise<SellingPrice> {
  const { data } = await api.patch<SellingPriceDetailResponse>(`/products/selling-prices/${id}/`, payload)
  return data.data
}

export async function deleteSellingPrice(id: number): Promise<void> {
  await api.delete(`/products/selling-prices/${id}/`)
}

// Product Quantities (legacy)
export async function fetchProductQuantities(): Promise<ProductQuantity[]> {
  const { data } = await api.get<ProductQuantitiesResponse>("/products/quantity/")
  return data.data
}

// Products
export async function fetchProducts(): Promise<Product[]> {
  const { data } = await api.get<ProductsResponse>("/products/")
  return data.data
}

// Available Products (public, no auth required)
export async function fetchAvailableProducts(): Promise<AvailableProduct[]> {
  const { data } = await axios.get<AvailableProductsResponse>(`${API_BASE_URL}/products/available-products/`)
  return data.data
}

// Product Request (public, no auth required)
export async function sendProductRequest(payload: ProductRequestPayload): Promise<string> {
  const { data } = await axios.post<ProductRequestResponse>(`${API_BASE_URL}/products/requests/`, payload)
  return data.message
}

// Sells
export async function fetchSells(): Promise<Sell[]> {
  const { data } = await api.get<SellsResponse>("/products/sell/")
  return data.data
}

export async function createSell(payload: CreateSellPayload): Promise<Sell> {
  const { data } = await api.post<SellDetailResponse>("/products/sell/", payload)
  return data.data
}

export async function deleteSell(id: number): Promise<void> {
  await api.delete(`/products/movement/${id}/`)
}

// Review Price
export interface ReviewPricePayload {
  text: number[]
  profit?: number | null
  selling_type?: number | null
}

export interface ReviewPriceResponse {
  status: number
  message: string
  data: {
    text: number[]
    profit: number
    calculated_price: number
  }
}

export async function reviewPrice(payload: ReviewPricePayload): Promise<ReviewPriceResponse['data']> {
  const { data } = await api.post<ReviewPriceResponse>("/products/review-price/", payload)
  return data.data
}

// Budget Calculation Prices
export async function fetchBudgetCalculationPrices(): Promise<BudgetCalculationPrice[]> {
  const { data } = await api.get<BudgetCalculationPricesResponse>("/products/budget-calculation-price/")
  return data.data
}

export async function updateBudgetCalculationPrice(
  id: number,
  payload: UpdateBudgetCalculationPricePayload
): Promise<BudgetCalculationPrice> {
  const { data } = await api.patch<BudgetCalculationPriceDetailResponse>(
    `/products/budget-calculation-price/${id}/`,
    payload
  )
  return data.data
}

// Material Colors
export async function fetchMaterialColors(): Promise<MaterialColor[]> {
  const { data } = await api.get<MaterialColorsResponse>("/materials/colors/")
  return data.data
}

// Wire Types
export async function fetchWireTypes(): Promise<WireType[]> {
  const { data } = await api.get<WireTypesResponse>("/materials/wire-types/")
  return data.data
}
